import React, { useCallback, type SyntheticEvent, useMemo } from 'react';
import { xcss, Box } from '@atlaskit/primitives';
import { asyncInlineEditStatusModal } from '@atlassian/jira-inline-edit-status-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { defaultModalProps } from '@atlassian/jira-status-config-common/src/constants.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { STATUS_TYPE } from '@atlassian/jira-issue-agg-field-transformers/src/common/constants.tsx';
import { editStatusExperience } from '../../../../experiences.tsx';
import { useListViewRefreshCacheActions } from '../../../../controllers/list-view-refresh-cache/ListViewRefreshCacheStore.tsx';
import { useGroupBy } from '../../../../controllers/group-by/index.tsx';
import messages from './messages.tsx';
import type { EditStatusButtonProps } from './types.tsx';

export const EditStatusButton = ({
	statusNames,
	status,
	statuses,
	projectId,
	projectKey,
	projectType,
	issueTypeId,
	shouldReturnFocus,
	buttonRef = null,
	analyticsSource,
	onEditStatusSuccess,
}: EditStatusButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [groupByFieldId] = useGroupBy();
	const isGroupingByStatus = groupByFieldId === STATUS_TYPE;
	const {
		triggerListViewCacheRefresh,
		triggerListViewGroupByCacheRefresh,
		triggerWorkflowCacheRefresh,
	} = useListViewRefreshCacheActions();

	const onStatusUpdated = useCallback(async () => {
		await triggerListViewCacheRefresh();
		await triggerWorkflowCacheRefresh();
		if (isGroupingByStatus) {
			await triggerListViewGroupByCacheRefresh();
		}
		onEditStatusSuccess && (await onEditStatusSuccess());
	}, [
		isGroupingByStatus,
		onEditStatusSuccess,
		triggerListViewCacheRefresh,
		triggerWorkflowCacheRefresh,
		triggerListViewGroupByCacheRefresh,
	]);

	const handleClick = useCallback(
		(e: SyntheticEvent) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'editStatusButton',
				{ source: analyticsSource },
			);

			// Because we are triggering the click manually for keyboard users
			// this handler becomes the source-of-truth handler for both keyboard users and mouse users
			// However, we need to stopPropagation from here to prevent double modal for mouse users
			// When mouse user clicks the button, if we don't stop propagation here, handleTransitionChange
			// will be called which will trigger click manually again and cause double modal rendering
			e.stopPropagation();
		},
		[analyticsSource, createAnalyticsEvent],
	);

	const onModalClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'closed',
				actionSubject: 'inlineEditStatusModal',
			}),
			{ source: analyticsSource },
		);
	}, [analyticsSource, createAnalyticsEvent]);

	const entryPointProps = useMemo(
		() => ({
			statusNames,
			projectId,
			projectKey,
			projectType,
			onClose: onModalClose,
			issueTypeId: issueTypeId ?? '',
			onStatusUpdated,
			editStatusExperience,
			status,
			statuses,
			source: analyticsSource,
			shouldShowStatusSelect: true,
		}),
		[
			statusNames,
			projectId,
			projectKey,
			projectType,
			onModalClose,
			issueTypeId,
			onStatusUpdated,
			analyticsSource,
			status,
			statuses,
		],
	);

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			interactionName="edit-status-modal"
			modalProps={{ ...defaultModalProps, shouldReturnFocus }}
			forwardedRef={buttonRef}
			useInternalModal
		>
			{({ ref }) => (
				// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
				<Box
					onClick={handleClick}
					backgroundColor="color.background.neutral.subtle"
					xcss={[editStatusInlineStyles]}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={ref as React.Ref<HTMLButtonElement>}
				>
					{formatMessage(messages.editStatus)}
				</Box>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const editStatusInlineStyles = xcss({
	display: 'flex',
	width: '100%',
	padding: 'space.150',
	color: 'color.text',
	fontWeight: 'font.weight.medium',
});
